import type { AuthTokens } from '@/stores/authStore';

/**
 * Story 123.3 — shared Tessera session bridge for federated remotes.
 *
 * Federated remotes (story 123.4 placeholder, eventually 123.5 gedu-web)
 * load as separate ESM modules and therefore have their own Zustand
 * stores. Re-doing OIDC inside the remote would double-prompt the
 * user and double-issue tokens. Instead the host shell publishes the
 * current Tessera access token + a small auth surface on a fixed
 * global symbol; remotes read from it.
 *
 * **Why a global, not a React context?** A remote loaded via
 * `await import(url)` does not share a React tree with the host —
 * its `useContext` would yield `undefined`. A global is the only
 * shape that crosses the loader boundary cleanly. The shape is
 * intentionally minimal (read-only token + tenant id + a refresh
 * callback) so the surface area is auditable.
 *
 * **Stability.** Treat this as part of the host↔remote contract
 * (parallel to the Kairos `kairos-platform-core` contract on the
 * backend, story 120). Bumping a field shape is a breaking change
 * for every remote in the registry.
 */
/**
 * The read-only session data a remote observes. Emitted to `subscribe`
 * listeners on change (ADR-015). Kept minimal and auditable.
 */
export interface KairosSessionSnapshot {
  readonly tokens: AuthTokens | null;
  readonly tenantId: string | null;
  /**
   * Effective (chosen) role. Present so a plugin that lets the HOST own
   * identity (ADR-015 `ownsIdentity`) can react when the host switches role,
   * without rendering its own role control.
   */
  readonly role: string | null;
  /** Active UI language (e.g. `es` / `en` / `pt-BR`), so a host-owned-identity plugin re-renders on language change. */
  readonly language: string | null;
}

export interface KairosSessionBridge extends KairosSessionSnapshot {
  /**
   * Best-effort refresh; remotes call this when they get a 401 from
   * Kairos. The host re-runs its own Tessera flow.
   */
  refresh: () => Promise<void>;
  /**
   * Resolves with the tokens once they are present, rejecting after
   * `timeoutMs`. The host publishes the bridge object synchronously, but
   * the access token can still be null for a tick (hydration / refresh in
   * flight). Without this, every remote has to poll the bridge itself
   * (the Educatio remote did exactly that). `whenReady()` centralises that
   * wait so a remote can simply `await __KAIROS_SESSION__.whenReady()`
   * before its first authenticated call. Polls the global, so it observes
   * republished updates, not a stale snapshot.
   */
  whenReady: (timeoutMs?: number) => Promise<AuthTokens>;
  /**
   * Subscribe to SUBSEQUENT session changes — token refresh, role switch,
   * language change, logout (ADR-015). Returns an unsubscribe fn. The listener
   * fires only when the snapshot actually changes (the host republishes on
   * every render; this de-dupes), NOT immediately on subscribe — read the
   * current values from the bridge fields synchronously, then use this for
   * updates. Listeners are held at module scope, so they survive the host's
   * per-render republish of the bridge object.
   */
  subscribe: (listener: (snapshot: KairosSessionSnapshot) => void) => () => void;
}

/** Data surface a caller publishes; `role`/`language` are optional (default null). */
export interface PublishSessionInput {
  tokens: AuthTokens | null;
  tenantId: string | null;
  role?: string | null;
  language?: string | null;
  refresh: () => Promise<void>;
}

declare global {
  var __KAIROS_SESSION__: KairosSessionBridge | undefined;
}

const READY_POLL_MS = 50;
const READY_DEFAULT_TIMEOUT_MS = 10_000;

/**
 * Polls the published bridge until its `tokens` are present (or `timeoutMs`
 * elapses). Reads `globalThis.__KAIROS_SESSION__` each tick so it sees the
 * host's republished session, not a captured snapshot. Exported for tests and
 * for host-side callers; remotes reach it as `__KAIROS_SESSION__.whenReady`.
 */
export function whenSessionReady(timeoutMs = READY_DEFAULT_TIMEOUT_MS): Promise<AuthTokens> {
  const current = globalThis.__KAIROS_SESSION__?.tokens;
  if (current) return Promise.resolve(current);
  return new Promise<AuthTokens>((resolve, reject) => {
    const startedAt = Date.now();
    const timer = setInterval(() => {
      const tokens = globalThis.__KAIROS_SESSION__?.tokens;
      if (tokens) {
        clearInterval(timer);
        resolve(tokens);
      } else if (Date.now() - startedAt >= timeoutMs) {
        clearInterval(timer);
        reject(new Error('__KAIROS_SESSION__ tokens not ready within timeout'));
      }
    }, READY_POLL_MS);
  });
}

// Listeners live at module scope (NOT on the bridge object, which the host
// replaces on every render) so a remote's subscription survives republishes.
const listeners = new Set<(snapshot: KairosSessionSnapshot) => void>();
let lastSnapshotKey: string | null = null;

function subscribeSession(listener: (snapshot: KairosSessionSnapshot) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

// A stable identity key for change detection — token identity + tenant/role/lang.
function snapshotKey(s: KairosSessionSnapshot): string {
  return JSON.stringify([s.tokens?.accessToken ?? null, s.tenantId, s.role, s.language]);
}

// Set the global and notify subscribers only if the observable snapshot changed
// (the host republishes on every render; this de-dupes).
function commit(snapshot: KairosSessionSnapshot, bridge: KairosSessionBridge | undefined): void {
  globalThis.__KAIROS_SESSION__ = bridge;
  const key = snapshotKey(snapshot);
  if (key === lastSnapshotKey) return;
  lastSnapshotKey = key;
  for (const listener of [...listeners]) {
    try {
      listener(snapshot);
    } catch {
      // A misbehaving remote listener must not break the host or sibling listeners.
    }
  }
}

/**
 * Publish the session. Callers pass the data surface
 * (`tokens`/`tenantId`/`refresh`, optionally `role`/`language`); the bridge's
 * `whenReady` and `subscribe` helpers are attached here so every call site
 * exposes them without repeating the implementation. Subscribers are notified
 * only when the observable snapshot changes.
 */
export function publishSession(input: PublishSessionInput): void {
  const snapshot: KairosSessionSnapshot = {
    tokens: input.tokens,
    tenantId: input.tenantId,
    role: input.role ?? null,
    language: input.language ?? null,
  };
  commit(snapshot, {
    ...snapshot,
    refresh: input.refresh,
    whenReady: whenSessionReady,
    subscribe: subscribeSession,
  });
}

const EMPTY_SNAPSHOT: KairosSessionSnapshot = {
  tokens: null,
  tenantId: null,
  role: null,
  language: null,
};

export function clearSession(): void {
  // Notify subscribers of logout too (change-detected: no-op if already empty).
  commit(EMPTY_SNAPSHOT, undefined);
}

/** Subscribe from the host side / tests; remotes reach it as `__KAIROS_SESSION__.subscribe`. */
export { subscribeSession };

export function getSession(): KairosSessionBridge | undefined {
  return globalThis.__KAIROS_SESSION__;
}
